import {
  type Channel,
  getVaapiChannelLabel,
} from '../../utils/get-channels-from-pypes';

/**
 * What a channel row's kebab offers (PSTUDIO-2064). The Pype name stopped being
 * user-facing: the Channel Name is what users see, and the backend pype's name
 * and id are only reachable from here, labelled "(Backend)".
 *
 * Pure so the menu can be pinned without rendering the channels page.
 */
export type ChannelMenuAction =
  | 'copyEmbedCode'
  | 'openPypeNext'
  | 'copyAppId'
  | 'copyPhone'
  | 'copyPypeId'
  | 'copyPypeName'
  | 'delete';

export type ChannelMenuItem = {
  action: ChannelMenuAction;
  /** i18n key under `pro-studio/channels:menu`; `defaultLabel` is the English. */
  labelKey: string;
  defaultLabel: string;
  icon: string;
  /** What a copy action puts on the clipboard. */
  value?: string;
  variant?: 'danger';
};

export const getChannelPhoneNumber = (channel: Channel): string | undefined =>
  channel.type === 'twilio_voice'
    ? channel.twilioVoiceConfig?.phone_number || undefined
    : undefined;

/** The channel type, shown in the Style column's tooltip. */
export const getChannelTypeLabel = (channel: Channel): string => {
  if (channel.type === 'twilio_voice') return 'Voice AI';
  if (channel.type === 'vaapi')
    return getVaapiChannelLabel(channel.vaapiChannelType);
  return 'Pype';
};

const backendItems = (channel: Channel): ChannelMenuItem[] => [
  {
    action: 'copyPypeId',
    labelKey: 'menu.copyPypeId',
    defaultLabel: 'Copy Pype ID (Backend)',
    icon: 'copy',
    value: channel.pypeId,
  },
  {
    action: 'copyPypeName',
    labelKey: 'menu.copyPypeName',
    defaultLabel: 'Copy Pype Name (Backend)',
    icon: 'copy',
    value: channel.parent,
  },
];

const deleteItem: ChannelMenuItem = {
  action: 'delete',
  labelKey: 'menu.delete',
  defaultLabel: 'Delete Channel',
  icon: 'delete',
  variant: 'danger',
};

export const getChannelMenuItems = (
  channel: Channel,
  {
    isViewer,
    isPypeNextEnabled,
  }: { isViewer: boolean; isPypeNextEnabled: boolean }
): ChannelMenuItem[] => {
  // Contact Center channels are managed in Contact Center: nothing to do here.
  if (channel.type === 'vaapi') return [];

  const tail = isViewer ? [] : [deleteItem];

  if (channel.type === 'twilio_voice') {
    const phone = getChannelPhoneNumber(channel);
    return [
      ...(phone
        ? [
            {
              action: 'copyPhone' as const,
              labelKey: 'menu.copyPhone',
              defaultLabel: `Copy ${phone}`,
              icon: 'copy',
              value: phone,
            },
          ]
        : []),
      ...backendItems(channel),
      ...tail,
    ];
  }

  return [
    {
      action: 'copyEmbedCode',
      labelKey: 'menu.copyEmbedCode',
      defaultLabel: 'Copy embed code',
      icon: 'tag',
    },
    ...(isPypeNextEnabled
      ? [
          {
            action: 'openPypeNext' as const,
            labelKey: 'menu.openPypeNext',
            defaultLabel: 'Pype Next',
            icon: 'edit',
          },
        ]
      : []),
    {
      action: 'copyAppId',
      labelKey: 'menu.copyAppId',
      defaultLabel: 'Copy App (Channel) ID',
      icon: 'copy',
      value: channel.id,
    },
    ...backendItems(channel),
    ...tail,
  ];
};
